import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import SearchIcon from "@mui/icons-material/Search";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { toast } from "react-toastify";
import ExecutiveOverview from "./components/ExecutiveOverview";
import PaymentPlatformAddCompanyFlow from "./components/PaymentPlatformAddCompanyFlow";
import PaymentPlatformExecutiveTable from "./components/PaymentPlatformExecutiveTable";
import {
  exportPaymentPlatformEntities,
  deletePaymentPlatformEntity,
  getPaymentPlatformEntity,
  getPaymentPlatformFullUnderwritingJob,
  getPaymentPlatformOverview,
  searchPaymentPlatformEntities,
  startPaymentPlatformFullUnderwriting,
} from "./api/companyApi";
import { buildPaymentPlatformCopilotContext } from "./utils/paymentPlatformCopilotContext";
import { downloadPaymentPlatformReport } from "./utils/paymentPlatformReportPdf";

const fmt = (value) => Number(value || 0).toLocaleString();
const fmtMoney = (value) => value == null
  ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(Number(value) || 0);
const fmtAverage = (value, suffix = "") => value == null ? "—" : `${Number(value).toFixed(1)}${suffix}`;
const fmtFullMoney = (value) => value == null ? "—" : `$${Number(value || 0).toLocaleString()}`;
const fmtPercent = (value) => value == null ? "—" : `${(Number(value) * 100).toFixed(1)}%`;

const FINISHED_UCC_STATUSES = new Set(["complete", "completed", "completed_with_errors", "done", "succeeded", "success"]);
const WAITING_UCC_STATUSES = new Set(["queued", "pending", "submitted", "accepted", "waiting"]);

export function buildImportProcessingStatus(job) {
  if (!job?.id) return null;
  const stage = String(job.stage || job.status || "queued").toLowerCase();
  const runnerStatus = String(job.uccLiensRunnerStatus || "").toLowerCase();
  const uccResultStatus = String(job.uccLiens?.status || "").toLowerCase();
  const uccSkipped = ["not_configured", "empty"].includes(uccResultStatus);
  const underwriting = job.underwritingProgress || null;
  const totalUnderwriting = Number(underwriting?.total || job.queuedUnderwritings || 0);
  const completedUnderwriting = Number(underwriting?.completed || 0);
  const filesDone = !["queued", "reading_files"].includes(stage);
  const uccDone = stage === "completed" || FINISHED_UCC_STATUSES.has(runnerStatus) || Boolean(job.uccLiens);
  const underwritingDone = totalUnderwriting === 0
    ? stage === "completed"
    : Boolean(underwriting && !underwriting.active && completedUnderwriting >= totalUnderwriting);
  const steps = [
    { label: "Files & matching", status: filesDone ? "done" : "active" },
    { label: uccSkipped ? "UCC & liens skipped" : "UCC & liens", status: uccDone ? "done" : stage === "ucc_liens" ? "active" : "pending" },
    { label: "Underwriting", status: underwritingDone ? "done" : underwriting?.active || (stage === "completed" && totalUnderwriting > 0) ? "active" : "pending" },
  ];

  if (job.status === "failed") return { tone: "error", title: "Processing stopped", detail: job.error || "The latest import could not be completed.", steps, active: false, progress: null };
  if (["queued", "reading_files"].includes(stage)) return { tone: "info", title: "Preparing uploaded files", detail: `Reading ${Number(job.filesRead || 0)} of ${Number(job.totalFiles || 0)} files.`, steps, active: true, progress: null };
  if (stage === "matching_entities") return { tone: "info", title: "Matching company records", detail: "Resolving duplicate rows and connecting new data to existing companies.", steps, active: true, progress: null };
  if (stage === "assessing") return { tone: "info", title: "Preparing companies for verification", detail: `${Number(job.assessedEntities || 0).toLocaleString()} of ${Number(job.matchedEntities || 0).toLocaleString()} companies prepared.`, steps, active: true, progress: null };
  if (stage === "ucc_liens") {
    if (!runnerStatus) return { tone: "info", title: "Sending companies to UCC & liens", detail: `${Number(job.uccLiensSubmitted || 0).toLocaleString()} companies are being submitted in one batch.`, steps, active: true, progress: null };
    if (WAITING_UCC_STATUSES.has(runnerStatus)) return { tone: "info", title: "Sent to UCC & liens — waiting for reply", detail: `${Number(job.uccLiensSubmitted || 0).toLocaleString()} companies were submitted. The dashboard is checking for results automatically.`, steps, active: true, progress: null };
    if (!FINISHED_UCC_STATUSES.has(runnerStatus)) return { tone: "info", title: "UCC & liens checks are running", detail: `The verification service reports “${runnerStatus.replaceAll("_", " ")}”. Results will be saved to each company.`, steps, active: true, progress: null };
  }
  if (underwriting?.active || (stage === "completed" && totalUnderwriting > completedUnderwriting)) {
    const running = Number(underwriting?.running || 0);
    const queued = Number(underwriting?.queued || Math.max(totalUnderwriting - completedUnderwriting, 0));
    return {
      tone: "info",
      title: running ? "Running full underwriting" : "UCC results received — underwriting queued",
      detail: `${completedUnderwriting.toLocaleString()} of ${totalUnderwriting.toLocaleString()} reports complete${running ? ` · ${running.toLocaleString()} running` : ""}${queued ? ` · ${queued.toLocaleString()} waiting` : ""}.`,
      steps,
      active: true,
      progress: totalUnderwriting ? Math.round((completedUnderwriting / totalUnderwriting) * 100) : null,
    };
  }
  return {
    tone: underwriting?.error ? "warning" : "success",
    title: "Processing complete",
    detail: totalUnderwriting
      ? `${completedUnderwriting.toLocaleString()} of ${totalUnderwriting.toLocaleString()} underwriting reports finished${underwriting?.error ? ` · ${Number(underwriting.error).toLocaleString()} need attention` : ""}.`
      : uccSkipped
        ? "Company data was saved. UCC & liens were unavailable or no eligible companies were supplied for this run."
        : "Company data and available verification results have been saved.",
    steps,
    active: false,
    progress: 100,
  };
}

function ImportProcessingStrip({ job }) {
  const process = buildImportProcessingStatus(job);
  if (!process) return null;
  const tones = {
    info: { border: "#b9d5ff", bg: "#f5f9ff", dot: "#2f72df" },
    success: { border: "#b8e0c9", bg: "#f4fbf7", dot: "#20945a" },
    warning: { border: "#f0d49b", bg: "#fffaf0", dot: "#cf8612" },
    error: { border: "#efb8b8", bg: "#fff6f6", dot: "#d14343" },
  };
  const tone = tones[process.tone] || tones.info;
  return (
    <Paper
      variant="outlined"
      data-payment-platform-import-progress
      sx={{ mx: { xs: 1.5, md: 3 }, mb: 2, p: { xs: 1.5, md: 1.75 }, borderRadius: 3, borderColor: tone.border, bgcolor: tone.bg }}
    >
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#61708a", fontSize: 10.5, fontWeight: 850, textTransform: "uppercase", letterSpacing: ".06em" }}>Live processing update</Typography>
          <Typography sx={{ color: "#17233f", fontSize: 15, fontWeight: 850, mt: .2 }}>{process.title}</Typography>
          <Typography sx={{ color: "#5e6b82", fontSize: 12.5, mt: .25 }}>{process.detail}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: .75, flexWrap: "wrap", flexShrink: 0 }}>
          {process.steps.map((step, index) => {
            const active = step.status === "active";
            const done = step.status === "done";
            return (
              <Box key={step.label} sx={{ display: "flex", alignItems: "center", gap: .65, px: 1, py: .65, borderRadius: 2, border: "1px solid", borderColor: active ? tone.border : "#dfe6ef", bgcolor: active ? "#fff" : "rgba(255,255,255,.55)" }}>
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: done || active ? tone.dot : "#d8e0ea", color: "#fff", fontSize: 10, fontWeight: 900 }}>
                  {done ? "✓" : index + 1}
                </Box>
                <Typography sx={{ color: active ? "#244f91" : "#64748b", fontSize: 11.5, fontWeight: active ? 800 : 700 }}>{step.label}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      {process.active && <LinearProgress variant={process.progress == null ? "indeterminate" : "determinate"} value={process.progress ?? 0} sx={{ mt: 1.25, height: 4, borderRadius: 4 }} />}
    </Paper>
  );
}

export function PaymentPlatformFilteredSummary({ summary, loading }) {
  const values = summary || {};
  const cards = [
    ["Filtered merchants", fmt(values.entityCount), "Entire filtered portfolio"],
    ["Applications", fmt(values.applicationCount), "Across all matching merchants"],
    ["Approvals", fmt(values.approvedCount), `${fmt(values.approvalRate)}% of filtered merchants`],
    ["Average model score", fmtAverage(values.averageScore), `${fmtAverage(values.averageRiskScore)} average risk score`],
    ["Requested amount", fmtFullMoney(values.totalRequestedAmount), `${fmtAverage(values.averageCoverage, "%")} average evidence`],
    ["Recommended amount", fmtFullMoney(values.totalRecommendedAmount), `${fmt(values.highRiskCount)} high-risk merchants`],
  ];
  return (
    <Box
      data-mca-filtered-portfolio-summary
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" },
        gap: 1.25,
        mt: 2,
      }}
    >
      {cards.map(([label, value, detail]) => (
        <Paper key={label} variant="outlined" sx={{ p: 1.5, borderRadius: "12px", borderColor: "#e4e9f2", minWidth: 0 }}>
          <Typography sx={{ color: "#6c7890", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase" }}>{label}</Typography>
          <Typography sx={{ color: "#17233f", fontSize: 22, fontWeight: 800, lineHeight: 1.25, mt: .25 }}>{loading ? "…" : value}</Typography>
          <Typography sx={{ color: "#6c7890", fontSize: 11.5, mt: .25 }}>{loading ? "\u00a0" : detail}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

function LiberisRiskScorePivot({ job }) {
  const rows = Array.isArray(job?.riskScorePivot) ? job.riskScorePivot : [];
  const totals = job?.applicationPortfolioTotals;
  if (!rows.length || !totals) return null;
  const displayRows = [
    ...rows,
    {
      riskScore: "Total",
      recommendedApplications: totals.recommendedApplications,
      firstAdvanceAmount: totals.firstAdvanceAmount,
      averageAdvance: totals.averageAdvance,
      factorRate: totals.weightedFactorRate,
      holdbackPct: totals.weightedHoldbackPct,
      baseDefaultRate: totals.baseDefaultRate,
      stressDefaultRate: totals.stressDefaultRate,
      projectedOriginations12m: totals.projectedOriginations12m,
      baseNetRevenue12m: totals.baseNetRevenue12m,
    },
  ];
  const columns = [
    ["Risk score", (row) => row.riskScore],
    ["Recommended applications", (row) => fmt(row.recommendedApplications)],
    ["First advances", (row) => fmtFullMoney(row.firstAdvanceAmount)],
    ["Average advance", (row) => fmtFullMoney(Math.round(Number(row.averageAdvance || 0)))],
    ["Factor", (row) => Number(row.factorRate || 0).toFixed(2)],
    ["Holdback", (row) => fmtPercent(row.holdbackPct)],
    ["Base default", (row) => fmtPercent(row.baseDefaultRate)],
    ["Stress default", (row) => fmtPercent(row.stressDefaultRate)],
    ["12m originations", (row) => fmtFullMoney(Math.round(Number(row.projectedOriginations12m || 0)))],
    ["12m net revenue", (row) => fmtFullMoney(Math.round(Number(row.baseNetRevenue12m || 0)))],
  ];
  return (
    <Paper
      data-liberis-risk-score-pivot
      variant="outlined"
      sx={{ mt: 3, borderRadius: "18px", overflow: "hidden", borderColor: "#e4e9f2", boxShadow: "0 12px 30px rgba(35,54,91,.06)" }}
    >
      <Box sx={{ px: { xs: 2, md: 2.75 }, py: 2.5 }}>
        <Typography sx={{ color: "#17233f", fontWeight: 800, fontSize: 20, letterSpacing: "-.015em" }}>
          Recommended portfolio by risk score
        </Typography>
        <Typography sx={{ color: "#6c7890", fontSize: 13, mt: .4 }}>
          Application-level planning view. The headline above uses each merchant&apos;s latest offer; this table includes all recommended applications.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mt: 2 }}>
          {[
            ["Recommended applications", fmt(totals.recommendedApplications)],
            ["First-advance capacity", fmtFullMoney(totals.firstAdvanceAmount)],
            ["12-month projected originations", fmtFullMoney(Math.round(Number(totals.projectedOriginations12m || 0)))],
            ["Base expected loss", fmtFullMoney(Math.round(Number(totals.baseExpectedLoss12m || 0)))],
            ["Base net factor revenue", fmtFullMoney(Math.round(Number(totals.baseNetRevenue12m || 0)))],
          ].map(([label, value]) => (
            <Box key={label} sx={{ minWidth: 165, flex: "1 1 165px", p: 1.25, bgcolor: "#f7f9fc", borderRadius: "10px" }}>
              <Typography sx={{ color: "#6c7890", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase" }}>{label}</Typography>
              <Typography sx={{ color: "#17233f", fontSize: 19, fontWeight: 800, mt: .25 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ overflowX: "auto", borderTop: "1px solid #edf0f5" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1120 }}>
          <Box component="thead" sx={{ bgcolor: "#f7f9fc" }}>
            <Box component="tr">
              {columns.map(([label]) => (
                <Box component="th" key={label} sx={{ px: 1.5, py: 1.25, color: "#64748b", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", textAlign: label === "Risk score" ? "left" : "right", whiteSpace: "nowrap" }}>
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {displayRows.map((row) => {
              const isTotal = row.riskScore === "Total";
              return (
                <Box component="tr" key={row.riskScore} sx={{ bgcolor: isTotal ? "#eef4ff" : "#fff" }}>
                  {columns.map(([label, render]) => (
                    <Box component="td" key={label} sx={{ px: 1.5, py: 1.2, borderTop: "1px solid #edf0f5", color: "#17233f", fontSize: 12.5, fontWeight: isTotal ? 800 : 600, textAlign: label === "Risk score" ? "left" : "right", whiteSpace: "nowrap" }}>
                      {render(row)}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ px: { xs: 2, md: 2.75 }, py: 1.5, color: "#6c7890", fontSize: 11.5, borderTop: "1px solid #edf0f5" }}>
        Base case assumes 50% renewal take-up, no more than two advances per merchant, and 100% loss given default. Default rates are inactivity-linked planning estimates, not observed all-cause defaults.
      </Typography>
    </Paper>
  );
}

function PaymentPlatformPortfolioTable({ tenantId, refreshKey, onAddCompany, onCompanyDeleted }) {
  const navigate = useNavigate();
  const loadedTenantRef = useRef(null);
  const [result, setResult] = useState({ items: [], nextCursor: null, total: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [decision, setDecision] = useState("");
  const [risk, setRisk] = useState("");
  const [coverage, setCoverage] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState([null]);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");
  const [exportError, setExportError] = useState("");
  const [exportSort, setExportSort] = useState({ key: "score", direction: "desc" });
  const [refreshJobs, setRefreshJobs] = useState({});
  const [reportExports, setReportExports] = useState({});
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingEntityId, setDeletingEntityId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(0);
    setCursors([null]);
  }, [debouncedSearch, decision, risk, coverage, pageSize]);

  useEffect(() => {
    if (!tenantId) return undefined;
    let active = true;
    let retryTimer;
    const isInitialTenantLoad = loadedTenantRef.current !== tenantId;
    if (isInitialTenantLoad) {
      setLoading(true);
      setError("");
    }
    const params = {
      query: debouncedSearch || undefined,
      decision: decision || undefined,
      risk: risk || undefined,
      minCoverage: coverage || undefined,
      pageSize,
      cursor: cursors[page] || undefined,
      includeSummary: page === 0,
    };
    const load = (attempt = 0) => {
      searchPaymentPlatformEntities(tenantId, params)
        .then((data) => {
          if (!active) return;
          const activeUnderwriting = Object.fromEntries((data?.items || [])
            .filter((item) => ["queued", "running"].includes(String(item?.fullUnderwriting?.status || "").toLowerCase()) && item?.fullUnderwriting?.jobId)
            .map((item) => [item.entityId, {
              ...item.fullUnderwriting,
              entityId: item.entityId,
            }]));
          if (Object.keys(activeUnderwriting).length) {
            setRefreshJobs((current) => ({ ...activeUnderwriting, ...current }));
          }
          setResult((current) => ({
            ...(data || { items: [], nextCursor: null, total: null }),
            total: data?.total ?? current.total ?? null,
            summary: data?.summary || current.summary || null,
          }));
          loadedTenantRef.current = tenantId;
          setError("");
          setLoading(false);
        })
        .catch((requestError) => {
          if (!active) return;
          if (attempt < 2) {
            retryTimer = setTimeout(() => load(attempt + 1), 750 * (attempt + 1));
            return;
          }
          setError(requestError?.response?.data?.message || "Could not load the portfolio table.");
          setLoading(false);
        });
    };
    load();
    return () => {
      active = false;
      clearTimeout(retryTimer);
    };
  }, [tenantId, debouncedSearch, decision, risk, coverage, pageSize, page, cursors, refreshKey, reportRefreshKey]);

  const refreshJobEntries = useMemo(
    () => Object.entries(refreshJobs).filter(([, job]) => job?.jobId),
    [refreshJobs],
  );
  const refreshJobKey = refreshJobEntries
    .map(([entityId, job]) => `${entityId}:${job.jobId}`)
    .sort()
    .join("|");

  useEffect(() => {
    if (!tenantId || !refreshJobEntries.length) return undefined;
    let active = true;
    const poll = async () => {
      const updates = await Promise.all(refreshJobEntries.map(async ([entityId, job]) => {
        try {
          return [entityId, await getPaymentPlatformFullUnderwritingJob(tenantId, job.jobId)];
        } catch (requestError) {
          return [entityId, null];
        }
      }));
      if (!active) return;
      for (const [entityId, job] of updates) {
        if (!job) continue;
        if (job.status === "done") {
          setRefreshJobs((current) => {
            const next = { ...current };
            delete next[entityId];
            return next;
          });
          setReportRefreshKey((current) => current + 1);
          toast.success("Underwriting report refreshed");
        } else if (job.status === "error") {
          setRefreshJobs((current) => {
            const next = { ...current };
            delete next[entityId];
            return next;
          });
          toast.error(job.message || "The underwriting report refresh failed");
        } else {
          setRefreshJobs((current) => ({ ...current, [entityId]: job }));
        }
      }
    };
    poll();
    const timer = window.setInterval(poll, 4000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  // Job status updates must not restart the polling interval; job IDs define the active work.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, refreshJobKey]);

  const refreshCompanyReport = async (entityId) => {
    if (!entityId || refreshJobs[entityId]) return;
    setRefreshJobs((current) => ({ ...current, [entityId]: { status: "starting" } }));
    try {
      const job = await startPaymentPlatformFullUnderwriting(tenantId, entityId);
      setRefreshJobs((current) => ({ ...current, [entityId]: job }));
      toast.info("Refreshing evidence and running the underwriting prompt");
    } catch (requestError) {
      setRefreshJobs((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      toast.error(requestError?.response?.data?.message || "Could not refresh the underwriting report");
    }
  };

  const deleteCompany = async () => {
    if (!deleteTarget?.entityId || deletingEntityId) return;
    setDeletingEntityId(deleteTarget.entityId);
    try {
      await deletePaymentPlatformEntity(tenantId, deleteTarget.entityId);
      setDeleteTarget(null);
      setReportRefreshKey((current) => current + 1);
      await onCompanyDeleted?.();
      toast.success("Company deleted");
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || "Could not delete company");
    } finally {
      setDeletingEntityId(null);
    }
  };

  const nextPage = () => {
    if (!result.nextCursor) return;
    setCursors((current) => {
      const next = current.slice(0, page + 1);
      next[page + 1] = result.nextCursor;
      return next;
    });
    setPage((current) => current + 1);
  };

  const openCompany = (entityId) =>
    navigate(`/tenant-dashboard/${tenantId}/assessments/company/${entityId}`, {
      state: { reportOrigin: "dashboard" },
    });
  const exportPortfolio = useCallback(async ({ format, sortKey, sortDirection }) => {
    const blob = await exportPaymentPlatformEntities(tenantId, {
      query: debouncedSearch || undefined,
      decision: decision || undefined,
      risk: risk || undefined,
      minCoverage: coverage || undefined,
      format,
      sortKey,
      sortDirection,
    });
    const extension = format === "xlsx" ? "xlsx" : "csv";
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `crediarc-${tenantId}-mca-assessments-${new Date().toISOString().slice(0, 10)}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [tenantId, debouncedSearch, decision, risk, coverage]);
  const runPortfolioExport = async (format) => {
    setExportError("");
    setExporting(format);
    try {
      await exportPortfolio({ format, sortKey: exportSort.key, sortDirection: exportSort.direction });
    } catch (requestError) {
      setExportError(requestError?.response?.data?.message || requestError?.message || "Could not export the filtered portfolio.");
    } finally {
      setExporting("");
    }
  };
  const exportCompanyReport = async (entityId) => {
    if (!entityId || reportExports[entityId]) return;
    setReportExports((current) => ({ ...current, [entityId]: true }));
    try {
      const entity = await getPaymentPlatformEntity(tenantId, entityId);
      await downloadPaymentPlatformReport(entity);
      toast.success("Company report downloaded");
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || requestError?.message || "Could not export the company report");
    } finally {
      setReportExports((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
    }
  };

  return (
    <Paper
      data-payment-platform-portfolio-table
      variant="outlined"
      sx={{ mt: 3, mb: 3, borderRadius: "18px", overflow: "hidden", borderColor: "#e4e9f2", boxShadow: "0 12px 30px rgba(35,54,91,.06)" }}
    >
      <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2.5, pb: 2, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ color: "#17233f", fontWeight: 700, fontSize: 20, letterSpacing: "-.015em" }}>Portfolio assessments</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              data-mca-portfolio-export
              startIcon={<FileDownloadOutlinedIcon />}
              variant="outlined"
              size="small"
              disabled={loading || !Number(result.total || result.items?.length) || Boolean(exporting)}
              onClick={() => runPortfolioExport("xlsx")}
              sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 800 }}
            >
              {exporting ? "Preparing XLS…" : "Export all to XLS"}
            </Button>
            <Button startIcon={<AddBusinessIcon />} variant="contained" size="small" onClick={onAddCompany} sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 700 }}>+ Add company</Button>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: "#6c7890", mt: .25, mb: 2 }}>
          Every Liberis merchant assessment, with full search, filtering and company reports.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, merchant ID, EIN, email, website or application…"
            sx={{ flex: "1 1 390px" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <TextField select size="small" label="Decision" value={decision} onChange={(event) => setDecision(event.target.value)} sx={{ minWidth: 145 }}>
            <MenuItem value="">All decisions</MenuItem>
            <MenuItem value="approve">Approve</MenuItem>
            <MenuItem value="review">Review</MenuItem>
            <MenuItem value="decline">Decline</MenuItem>
            <MenuItem value="insufficient_data">Insufficient data</MenuItem>
          </TextField>
          <TextField select size="small" label="Risk" value={risk} onChange={(event) => setRisk(event.target.value)} sx={{ minWidth: 125 }}>
            <MenuItem value="">All risk</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
          <TextField select size="small" label="Evidence" value={coverage} onChange={(event) => setCoverage(event.target.value)} sx={{ minWidth: 145 }}>
            <MenuItem value="">Any coverage</MenuItem>
            <MenuItem value="25">25%+</MenuItem>
            <MenuItem value="50">50%+</MenuItem>
            <MenuItem value="75">75%+</MenuItem>
          </TextField>
        </Box>
        <PaymentPlatformFilteredSummary summary={result.summary} loading={loading && page === 0} />
        {exportError && <Alert severity="error" onClose={() => setExportError("")} sx={{ mt: 1.5 }}>{exportError}</Alert>}
      </Box>
      {error ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => setReportRefreshKey((current) => current + 1)}>Retry</Button>}
          sx={{ m: 2 }}
        >
          {error}
        </Alert>
      ) : loading ? (
        <Box sx={{ py: 7, textAlign: "center" }}><CircularProgress size={30} /></Box>
      ) : (
        <PaymentPlatformExecutiveTable
          rows={result.items || []}
          onOpenCompany={openCompany}
          onRefreshCompany={refreshCompanyReport}
          onDeleteCompany={setDeleteTarget}
          refreshingEntityIds={Object.keys(refreshJobs)}
          deletingEntityIds={deletingEntityId ? [deletingEntityId] : []}
          onExportReport={exportCompanyReport}
          exportingReportEntityIds={Object.keys(reportExports)}
          onSortChange={setExportSort}
          filteredTotal={result.total}
          hideExport
          viewport="portfolio-assessments"
        />
      )}
      <Box sx={{ px: { xs: 2, md: 2.75 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderTop: "1px solid #edf0f5" }}>
        <Typography sx={{ color: "#6c7890", fontSize: 13 }}>
          Page {page + 1}{result.total != null ? ` · ${fmt(result.total)} companies` : ""}{result.searchLimited ? " · first 1,000 search matches" : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField select size="small" label="Rows" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} sx={{ width: 90 }}>
            <MenuItem value={25}>25</MenuItem><MenuItem value={50}>50</MenuItem><MenuItem value={100}>100</MenuItem>
          </TextField>
          <Button variant="outlined" disabled={page === 0 || loading} onClick={() => setPage((current) => Math.max(0, current - 1))}>Previous</Button>
          <Button variant="contained" disabled={!result.nextCursor || loading} onClick={nextPage}>Next</Button>
        </Box>
      </Box>
      <Dialog open={Boolean(deleteTarget)} onClose={deletingEntityId ? undefined : () => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete company?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#475569" }}>
            This permanently deletes <strong>{deleteTarget?.identity?.companyName || "this company"}</strong>, its report, assessment history, and related underwriting data. Any active underwriting will be cancelled. The company can reappear if its source file is imported again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={Boolean(deletingEntityId)} onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={Boolean(deletingEntityId)} onClick={deleteCompany}>
            {deletingEntityId ? "Deleting…" : "Delete company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default function PaymentPlatformDashboardPage() {
  const navigate = useNavigate();
  const { setPortfolioCopilotContext } = useOutletContext() || {};
  const { tenantIdp } = useParams();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [portfolioRefreshKey, setPortfolioRefreshKey] = useState(0);

  const load = useCallback(async ({ showLoading = true } = {}) => {
    if (!tenantIdp) return;
    if (showLoading) {
      setLoading(true);
      setError("");
    }
    try {
      const overviewData = await getPaymentPlatformOverview(tenantIdp);
      setOverview(overviewData);
    } catch (requestError) {
      if (showLoading) setError(requestError?.response?.data?.message || "Could not load the payment-platform dashboard.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [tenantIdp]);

  const refreshPortfolio = useCallback(async () => {
    setPortfolioRefreshKey((current) => current + 1);
    await load({ showLoading: false });
  }, [load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const refreshAfterImportStarts = (event) => {
      if (event?.detail?.tenantId !== tenantIdp) return;
      load({ showLoading: false });
      setPortfolioRefreshKey((current) => current + 1);
    };
    window.addEventListener("payment-platform-import-started", refreshAfterImportStarts);
    return () => {
      window.removeEventListener("payment-platform-import-started", refreshAfterImportStarts);
    };
  }, [load, tenantIdp]);
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      load({ showLoading: false });
      setPortfolioRefreshKey((current) => current + 1);
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [load]);
  const latestImportStatus = overview?.latestImport?.status;
  const underwritingActive = Boolean(overview?.latestImport?.underwritingProgress?.active);
  useEffect(() => {
    if (!tenantIdp || (!['queued', 'processing'].includes(latestImportStatus) && !underwritingActive)) return undefined;
    const timer = window.setInterval(() => {
      load({ showLoading: false });
      setPortfolioRefreshKey((current) => current + 1);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [latestImportStatus, load, tenantIdp, underwritingActive]);
  useEffect(() => {
    if (!setPortfolioCopilotContext || !tenantIdp) return undefined;
    const data = buildPaymentPlatformCopilotContext(overview);
    setPortfolioCopilotContext({ tenantId: tenantIdp, data });
    return () => setPortfolioCopilotContext((current) => (
      current?.tenantId === tenantIdp && current?.data?.workspace === "MCA / Loan Assessments"
        ? null
        : current
    ));
  }, [overview, setPortfolioCopilotContext, tenantIdp]);

  const job = overview?.latestImport;
  const livePortfolio = overview?.livePortfolio;
  const hasLivePortfolio = livePortfolio?.entityCount != null;
  const decisions = (hasLivePortfolio ? livePortfolio?.decisionDistribution : job?.decisionDistribution) || {};
  const risks = (hasLivePortfolio ? livePortfolio?.riskDistribution : job?.riskDistribution) || {};
  const aggregates = hasLivePortfolio
    ? {
      ...(job?.aggregates || {}),
      ...livePortfolio,
      pdScoredEntities: Number(livePortfolio.entityCount || 0) ? job?.aggregates?.pdScoredEntities : 0,
    }
    : (job?.aggregates || {});
  const matched = Number(hasLivePortfolio ? livePortfolio.entityCount : job?.matchedEntities || 0);
  const assessed = Number(hasLivePortfolio ? livePortfolio.entityCount : job?.assessedEntities || 0);
  const decisionTotal = Object.values(decisions).reduce((sum, value) => sum + Number(value || 0), 0);
  const approvalRate = decisionTotal ? (Number(decisions.approve || 0) / decisionTotal) * 100 : 0;
  const riskTotal = Number(risks.low || 0) + Number(risks.medium || 0) + Number(risks.high || 0);
  const fallbackRiskScore = riskTotal
    ? (Number(risks.low || 0) * 2.5 + Number(risks.medium || 0) * 6 + Number(risks.high || 0) * 8.5) / riskTotal
    : 0;
  const portfolioScore = assessed === 0
    ? null
    : aggregates.averageRiskScore == null
    ? fallbackRiskScore
    : Number(aggregates.averageRiskScore) / 10;
  const scrollToPortfolio = useCallback(() => {
    document.querySelector("[data-payment-platform-portfolio-table]")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const metricCards = useMemo(() => [
    {
      eyebrow: "Assessed merchants",
      value: fmt(assessed || matched),
      detail: `${fmt(job?.unmatchedRows)} merchants require additional identity review.`,
      accent: "#3168e0",
      icon: <BusinessRoundedIcon />,
      action: "View portfolio",
      onClick: scrollToPortfolio,
    },
    {
      eyebrow: tenantIdp === "liberis" ? "Approved merchants (latest)" : "Approvals",
      value: fmt(decisions.approve),
      detail: tenantIdp === "liberis"
        ? `${fmtAverage(approvalRate, "%")} of assessed merchants · latest application per MerchantID.`
        : `${fmtAverage(approvalRate, "%")} of assessed merchants · model v${overview?.model?.version || 1}.`,
      accent: "#35a477",
      icon: <CheckCircleRoundedIcon />,
      action: "View approved merchants",
      onClick: scrollToPortfolio,
    },
    {
      eyebrow: "Approved amount",
      value: fmtMoney(aggregates.approvedRecommendedAmount),
      detail: "Total recommended advance for approved merchants.",
      accent: "#22846a",
      icon: <MonetizationOnRoundedIcon />,
      action: "Review approved capacity",
      onClick: scrollToPortfolio,
    },
    {
      eyebrow: "Average PD score",
      value: fmtAverage(aggregates.averagePdScore),
      detail: aggregates.pdScoredEntities
        ? `Based on ${fmt(aggregates.pdScoredEntities)} merchants with an imported PD output.`
        : "Available when the import includes a mapped PD score output.",
      accent: "#7456c7",
      icon: <QueryStatsRoundedIcon />,
    },
    {
      eyebrow: "Average model score",
      value: fmtAverage(aggregates.averageScore),
      detail: `${fmtAverage(aggregates.averageCoverage, "%")} average evidence coverage.`,
      accent: "#3168e0",
      icon: <QueryStatsRoundedIcon />,
    },
    {
      eyebrow: "Decision queue",
      value: fmt(decisions.review),
      detail: `${fmt(decisions.decline)} declined · ${fmt(risks.high)} high-risk merchants.`,
      accent: "#d79a27",
      icon: <ScheduleRoundedIcon />,
      action: "Review decisions",
      onClick: scrollToPortfolio,
    },
  ], [assessed, matched, job?.unmatchedRows, decisions.review, decisions.decline, decisions.approve, risks.high, approvalRate, aggregates.approvedRecommendedAmount, aggregates.averagePdScore, aggregates.pdScoredEntities, aggregates.averageScore, aggregates.averageCoverage, overview?.model?.version, scrollToPortfolio, tenantIdp]);

  if (loading) {
    return <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}><CircularProgress aria-label="Loading payment-platform dashboard" /></Box>;
  }
  if (error || !overview) {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
      >
        {error || "Dashboard data is unavailable."}
      </Alert>
    );
  }

  return (
    <Box data-payment-platform-dashboard>
      <ImportProcessingStrip job={job} />
      <ExecutiveOverview
        isProspects={false}
        portfolioScore={portfolioScore}
        prospectsCount={0}
        approvedLimit={0}
        existingCustomers={matched}
        customersAtRisk={Number(risks.high || 0)}
        moneyAtRisk={0}
        onReviewRisk={scrollToPortfolio}
        onLoadAging={load}
        scoreLabel="Average portfolio risk score"
        intelligenceLabel="Live MCA / loan intelligence"
        metricCards={metricCards}
      />
      {tenantIdp === "liberis" && <LiberisRiskScorePivot job={job} />}
      <PaymentPlatformPortfolioTable
        tenantId={tenantIdp}
        refreshKey={portfolioRefreshKey}
        onAddCompany={() => setAddOpen(true)}
        onCompanyDeleted={refreshPortfolio}
      />
      <PaymentPlatformAddCompanyFlow
        tenantId={tenantIdp}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setPortfolioRefreshKey((current) => current + 1);
          load();
        }}
        onOpenReport={(entityId) => navigate(`/tenant-dashboard/${tenantIdp}/assessments/company/${entityId}`, {
          state: { reportOrigin: "dashboard" },
        })}
      />
    </Box>
  );
}
